import equal from 'fast-deep-equal';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import type { ContentReferenceContext } from '@gitbook/app-database';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import { resolveURLToNavigation } from '@gitbook/app-navigation';
import {
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    getSectionsInDoc,
} from '@gitbook/doc-core';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import {
    createPalette,
    type PaletteCommandDef,
    type PaletteCommandTrigger,
    type PaletteDef,
    type PaletteGroupDef,
    type PalettePositionOptions,
} from '@gitbook/react-uikit';
import type { Document } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { createCache, getSuspense, mapSuspenseArray } from '@gitbook/util-suspense-cache';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';
import { isUrl, normalizeUrlLikeStringToUrl } from '@gitbook/util-url';

import { apiResource, excludeSiteExternalLinks, readAPICache } from '../../state';
import { CollectionIcon } from '../Collection';
import { searchOrganizationMembers } from '../Search';
import { SiteSectionGroupIcon, SiteSectionIcon } from '../Site';
import { SpaceIcon } from '../Space';
import { UserAvatar } from '../Users';
import { resolveContentRefCache, resolveScreenToContentRefCache } from './caches';

export type CreateInputContentRefPaletteOptions<T extends ContentRefKind[] = ContentRefKind[]> = {
    /**
     * List of accepted references
     */
    accept?: T;

    /**
     * Allow customizing the search input's placeholder and value on this palette.
     * If `search` it not provided, the default placeholder will be used.
     * */
    search?: {
        enabled?: boolean;
        placeholder?: string;
        initialValue?: string;
    };

    /** Sizing / position for the palette */
    position?: PalettePositionOptions;

    /**
     * Extra groups to be prepended to the beginning of the results.
     */
    extraGroupsAtStart?: CreateInputContentRefPaletteExtraGroup<T>[];

    /**
     * Extra groups to be appended to the end of the results.
     */
    extraGroups?: CreateInputContentRefPaletteExtraGroup<T>[];

    /**
     * Value if used in an input.
     */
    value?: api.ContentRef | null | undefined;

    /**
     * Constrain the palette height to the space next to its anchor (see PaletteDef).
     */
    constrainHeight?: boolean;
};

/**
 * Context passed into all functions.
 */
type ContentRefPaletteContext = Pick<
    ContentReferenceContext,
    'organization' | 'space' | 'page' | 'site'
> & {
    accept: ContentRefKind[];
    hiveClient: HiveClient;
    pick: (link: api.ContentRef) => void;
    document?: Document;
    firstPage?: string | undefined;
    changeRequest?: string;
    /**
     * Per-palette cache of computed revisions (see `createCompleteRevisionCache`). Optional so
     * single-space callers of `createSpacePagesGroup` can omit it; the cross-space flatten
     * provides one so it survives Suspense retries without the shared computer-cache eviction
     * loop.
     */
    completeRevisionCache?: ReturnType<typeof createCompleteRevisionCache>;
};

/**
 * Create a command palette to pick a content reference.
 */
export function createInputContentRefPalette<T extends ContentRefKind[]>(
    pick: (link: KindToContent[T[number]]) => void,
    contentRefContext: ContentReferenceContext & { document?: Document },
    hiveClient: HiveClient,
    options: CreateInputContentRefPaletteOptions<T> = {}
): PaletteDef {
    const { document, ...rawContext } = contentRefContext;
    const { accept = defaultAccept, position, value, constrainHeight } = options;
    const defaultSearch = { enabled: true, placeholder: 'Type a URL or search in your content...' };
    const search = { ...defaultSearch, ...options.search };
    const untypedPick = pick as (link: api.ContentRef) => void;
    const context: ContentRefPaletteContext = {
        organization: contentRefContext.organization,
        space: contentRefContext.space,
        changeRequest:
            'changeRequest' in contentRefContext ? contentRefContext.changeRequest : undefined,
        page: contentRefContext.page,
        site: contentRefContext.site,
        accept,
        hiveClient,
        pick: untypedPick,
        firstPage:
            (value?.kind === 'anchor' || value?.kind === 'page') &&
            (!value.space || value.space === contentRefContext.space)
                ? value.page
                : contentRefContext.page,
        // Scoped to this palette instance so it is released when the palette closes.
        completeRevisionCache: createCompleteRevisionCache(),
    };
    const groupMap = new Map<ContentRefKind, PaletteGroupDef | PaletteGroupDef[]>();

    // Get site structure palette groups, if applicable. The content and headings groups
    // both derive commands from the same site structure on every keystroke, so they share
    // a cache to avoid rebuilding a large site's command tree more than once per structure.
    const siteStructureCommandsCache = createSiteStructureCommandsCache(context);
    const siteGroup = getSitePaletteGroup(context, siteStructureCommandsCache);
    const siteHeadingsGroup = siteGroup
        ? getSiteHeadingsPaletteGroup(context, siteStructureCommandsCache)
        : null;

    /*
     * Input a raw url.
     */
    if (accept.includes('url')) {
        groupMap.set('url', {
            key: 'url',
            commands: (query) => {
                if (!query) {
                    return [];
                }

                if (isUrl(query)) {
                    // Try resolving to a content ref if it's a GitBook.com url
                    const screen = resolveURLToNavigation(query);

                    if (screen) {
                        const [contentRef] = resolveScreenToContentRefCache.read([
                            hiveClient,
                            screen,
                            rawContext,
                        ]);
                        const [resolved] = resolveContentRefCache.read([
                            hiveClient,
                            contentRef,
                            rawContext,
                        ]);

                        if (contentRef && resolved) {
                            return [
                                {
                                    key: query,
                                    title: `Link to "${resolved.text}"`,
                                    icon: Icon.Link,
                                    trigger: () => {
                                        untypedPick(contentRef);
                                    },
                                },
                            ];
                        }
                    }
                }

                const parsedHref = normalizeUrlLikeStringToUrl(query);

                if (!parsedHref) {
                    return [];
                }

                return [
                    {
                        key: query,
                        title: `Link to "${query}"`,
                        icon: Icon.Link,
                        trigger: () => {
                            untypedPick({ kind: 'url', url: parsedHref });
                        },
                    },
                ];
            },
        });
    }

    /*
     * Search anchors in the document.
     */
    if (
        accept.includes('anchor') &&
        document &&
        // If there is no value or if the value is an anchor on the current page.
        (!value || (value.kind === 'anchor' && (!value.page || value.page === context.page)))
    ) {
        // The palette holds one document for its lifetime, so its anchors are walked once on
        // first search rather than on every keystroke.
        let anchorCommands: PaletteCommandDef[] | null = null;

        groupMap.set('anchor', {
            title: 'Current page',
            key: 'anchors',
            // Pin matching current-page anchors into the local rank band (see SITE_CONTENT_RANK)
            // so they sort above site-wide matches.
            commands: (query) => {
                anchorCommands ??= getAnchorCommandsForDoc(document, {}, untypedPick);
                return withDirectTitleMatchPriority(anchorCommands, query);
            },
        });
    }

    /*
     * Search in the current space.
     */
    if (accept.includes('page') && context.space) {
        groupMap.set(
            'page',
            createSpacePagesGroup({
                content: {
                    spaceId: context.space,
                    changeRequestId: 'changeRequest' in context ? context.changeRequest : undefined,
                },
                context,
            })
        );
    }

    /*
     * Search for spaces.
     */
    if (accept.includes('space')) {
        groupMap.set('space', {
            title: 'Spaces',
            key: 'spaces',
            commands: (query) => {
                const allContent = readAPICache(
                    apiResource('getOrganizationAllContent', {
                        organizationId: context.organization,
                    }),
                    { staleWhileRevalidate: true }
                );

                return createOrganizationContentCommands({
                    items: allContent.spaces,
                    query,
                    context,
                });
            },
        });
    }

    /*
     * Search for users.
     */
    if (accept.includes('user')) {
        groupMap.set('user', {
            title: 'Users',
            key: 'users',
            commands: (query) => {
                const members = searchOrganizationMembers({
                    organizationId: context.organization,
                    query,
                    limit: 8,
                });

                return members.map((member) => ({
                    key: member.user.id,
                    icon: () => <UserAvatar user={member.user} size="md" tooltip={false} />,
                    title: member.user.displayName,
                    subtitle: member.user.email,
                    trigger: () => {
                        untypedPick({ kind: 'user', user: member.user.id });
                    },
                }));
            },
        });
    }

    // Order groups based on accept order
    const groups: PaletteGroupDef[] = accept.flatMap((kind) => {
        const group = groupMap.get(kind);
        return Array.isArray(group) ? group : group ? [group] : [];
    });

    // Append extra groups
    options.extraGroups?.forEach((group) =>
        groups.push(typeof group === 'function' ? group(untypedPick) : group)
    );

    // Prepend site group if it exists.
    if (siteGroup) {
        if (siteHeadingsGroup) {
            groups.unshift(siteHeadingsGroup);
        }
        groups.unshift(siteGroup);
    }

    // Prepend extra groups
    options.extraGroupsAtStart?.forEach((group) =>
        groups.unshift(typeof group === 'function' ? group(untypedPick) : group)
    );

    return createPalette({
        search: search.enabled
            ? { placeholder: search.placeholder, initialValue: search.initialValue }
            : undefined,
        groups,
        position,
        constrainHeight,
    });
}

/**
 * Stable key for the anchors (headings & tabs) group within a page descendant palette.
 * Path-query resolution keys the content/headings split off this rather than the display
 * title, so renaming the group's title can't silently break the split.
 */
const HEADINGS_GROUP_KEY = 'anchors';

/**
 * Create a descendant palette for a page, including anchors (headings and tabs),
 * subpages, and an optional link to the page itself.
 */
function createPageDescendantPalette(args: {
    page: hiveCore.RevisionPage;
    context: ContentRefPaletteContext;
    spaceContext: SpacePaletteContext;
}): PaletteDef | null {
    const { page, context, spaceContext } = args;
    const { accept, pick } = context;
    const { revision, createPageRef } = spaceContext;

    const placeholder =
        accept.includes('anchor') && accept.includes('page')
            ? 'Search heading, tab, or page…'
            : accept.includes('anchor')
              ? 'Search heading or tab…'
              : 'Search page…';

    const subPages =
        accept.includes('page') && hiveCore.isRevisionPageParent(page)
            ? hiveCore.getPageChildrenInRevision(revision, page.id)
            : [];

    const groups = [
        accept.includes('page')
            ? {
                  key: 'page',
                  commands: [
                      {
                          key: 'pick',
                          title: `Link to "${page.linkTitle || page.title}"`,
                          filterPriority: 1,
                          icon:
                              page.type === hiveCore.RevisionPageType.Group
                                  ? Icon.AddPageGroup
                                  : Icon.Page,
                          trigger: () => pick(createPageRef(page.id)),
                      },
                  ],
              }
            : null,
        accept.includes('anchor') && hiveCore.isRevisionPageDocument(page)
            ? {
                  title: 'Headings & tabs',
                  key: HEADINGS_GROUP_KEY,
                  commands: () => getAnchorCommands({ page, context, spaceContext }),
              }
            : null,
        subPages.length > 0
            ? {
                  title: 'Subpages',
                  key: `subpages-${page.id}`,
                  commands: createPageCommands({
                      pages: subPages,
                      context,
                      spaceContext,
                  }),
              }
            : null,
    ].filter(filterOutNullable);

    if (groups.length === 0) {
        return null;
    }

    return createPalette({
        search: { placeholder },
        groups,
    });
}

type SpacePaletteContext = {
    spaceId: string;
    changeRequestId: string | undefined;
    revision: hiveCore.Revision;
    pagePaths: Map<string, string>;
    createPageRef: (pageId: string) => api.ContentRefPage;
};

/**
 * Generate a palette group def for pages of a Space.
 */
export function createSpacePagesGroup(props: {
    content: {
        spaceId: string;
        changeRequestId: string | undefined;
    };
    context: ContentRefPaletteContext;
    /**
     * Return the full flattened page list without filtering, leaving the caller to
     * filter. Used by the cached flat site-structure list so the cache stays
     * query-independent instead of being frozen to the first query's matches.
     */
    flatten?: boolean;
}): PaletteGroupDef {
    const { content, context, flatten = false } = props;
    const { hiveClient } = context;
    const isCurrentSpace = context.space === content.spaceId;
    // The flat list depends only on the revision, so it is built once and filtered per query.
    // Rebuilding it per keystroke also rebuilt every page's descendant palette — which itself
    // materializes its subpages' commands, so the cost grew with the tree's depth — and handed
    // `filterContentReferenceCommands` fresh command objects each time, defeating the
    // normalization cache it filters through.
    let flatCommandsCache: {
        revision: hiveCore.Revision;
        commands: PaletteCommandDef[];
    } | null = null;

    return {
        title: isCurrentSpace ? 'Pages in current space' : 'Pages',
        key: 'space',
        commands: (query) => {
            // `computed: false` is required: getBranchRevision defaults to computed=true, which
            // runs getCompleteRevision *inside* getBranchRevision and does NOT cache the result —
            // that is the uncached call that hits the shared computer-cache eviction loop. We
            // fetch the source revision here and let completeRevisionCache do the (cached)
            // computation below, so the computed result survives LRU eviction.
            const sourceRevision = getSuspense(
                hiveClient.store.getBranchRevision(
                    content.spaceId,
                    content.changeRequestId
                        ? getChangeRequestBranchId(content.changeRequestId)
                        : hiveCore.CONTENT_DEFAULT_BRANCH,
                    { computed: false }
                )
            );

            if (!sourceRevision) {
                return [];
            }

            // Read the computed revision through the palette-owned cache when one is provided
            // (the cross-space flatten), rather than straight off the ContentComputer instance.
            // The flatten reads one computer per space, but `getContentComputer` hands them out
            // of a small shared LRU — on a large site the flatten needs more computers than the
            // cache holds, so instances are evicted mid-pass and a Suspense retry recreates a
            // cold one that suspends again, looping forever. The cache (keyed by content +
            // revision, not tied to the evictable instance) lets the retry resolve synchronously.
            // Single-space callers omit the cache and read directly — they never hit the loop.
            const { revision } = context.completeRevisionCache
                ? context.completeRevisionCache.read([hiveClient, sourceRevision])
                : getSuspense(
                      hiveClient
                          .getContentComputer(sourceRevision.contentId)
                          .getCompleteRevision(sourceRevision)
                  );

            // Use cached paths for pages to avoid expensive recomputation on every render
            const pagePaths = pagePathsMapCache.read(revision);

            const spaceContext: SpacePaletteContext = {
                ...content,
                revision,
                pagePaths,
                createPageRef: (pageId: string) => ({
                    kind: 'page',
                    page: pageId,
                    space: isCurrentSpace ? undefined : content.spaceId,
                }),
            };

            const rootPages = hiveCore.getRootPagesInRevision(revision);

            // In search mode we return all pages flat and let the menu filter.
            // When `flatten` is set the caller filters the cached list downstream, so
            // return the unfiltered flat list here — filtering now would drop pages that
            // don't match the current query before they reach the cache.
            if (flatten || query) {
                if (flatCommandsCache?.revision !== revision) {
                    flatCommandsCache = {
                        revision,
                        commands: getFlatPagesCommands({
                            pages: rootPages,
                            revision,
                            context,
                            spaceContext,
                        }),
                    };
                }

                const flatCommands = flatCommandsCache.commands;
                return flatten
                    ? flatCommands
                    : withDirectTitleMatchPriority(
                          filterContentReferenceCommands(query, flatCommands),
                          query
                      );
            }

            return createPageCommands({
                pages: rootPages,
                context,
                spaceContext,
            });
        },
    };
}

/**
 * Flatten all pages from a revision and convert them to commands.
 *
 * Query-independent: the matched-title ranking each query needs is applied afterwards by
 * {@link withDirectTitleMatchPriority}, so this list can be built once per revision.
 */
function getFlatPagesCommands(args: {
    revision: hiveCore.Revision;
    pages: hiveCore.RevisionPage[];
    ancestors?: hiveCore.RevisionPage[];
    spaceContext: SpacePaletteContext;
    context: ContentRefPaletteContext;
}) {
    const { revision, pages, context, spaceContext, ancestors = [] } = args;
    return pages.reduce<PaletteCommandDef[]>((commands, page) => {
        const command = createPageCommand({
            page,
            context,
            spaceContext,
        });

        if (command) {
            const children = hiveCore.isRevisionPageParent(page)
                ? hiveCore.getPageChildrenInRevision(revision, page.id)
                : [];

            commands.push(
                {
                    ...command,
                    parentTitles: ancestors.map((ancestor) => ancestor.title),
                },
                ...getFlatPagesCommands({
                    revision,
                    pages: children,
                    ancestors: [...ancestors, page],
                    context,
                    spaceContext,
                })
            );
        }

        return commands;
    }, []);
}

/**
 * Rank commands whose title matches the query, leaving the rest untouched.
 *
 * Non-matching commands are returned by identity so a cached command list keeps its place in the
 * per-command normalization cache that filtering reads.
 */
function withDirectTitleMatchPriority(
    commands: PaletteCommandDef[],
    query: string
): PaletteCommandDef[] {
    return commands.map((command) => {
        const filterPriority = getDirectTitleMatchPriority({
            title: typeof command.title === 'string' ? command.title : '',
            query,
            depth: command.parentTitles?.length ?? 0,
        });

        return filterPriority === undefined ? command : { ...command, filterPriority };
    });
}

function createPageCommands(args: {
    pages: hiveCore.RevisionPage[];
    spaceContext: SpacePaletteContext;
    context: ContentRefPaletteContext;
}): PaletteCommandDef[] {
    const { pages, spaceContext, context } = args;
    const { firstPage } = context;
    const orderedPages = firstPage ? orderPagesWithFirst(pages, firstPage) : pages;

    return orderedPages.reduce<PaletteCommandDef[]>((all, page) => {
        const command = createPageCommand({ page, spaceContext, context });
        if (command) {
            all.push(command);
        }
        return all;
    }, []);
}

/**
 * Create a command for revision page.
 */
function createPageCommand(args: {
    page: hiveCore.RevisionPage;
    spaceContext: SpacePaletteContext;
    context: ContentRefPaletteContext;
}): PaletteCommandDef | null {
    const { page, spaceContext, context } = args;
    const { accept, pick } = context;
    const { createPageRef, pagePaths } = spaceContext;

    // Exclude links.
    if (page.type === hiveCore.RevisionPageType.Link) {
        return null;
    }

    const descendantPalette = createPageDescendantPalette(args);

    // Compute page command attributes for correct typing,
    // if a page has no trigger and no descendant, then we don't show it.
    const attrs = getPaletteAttributes({
        descendant: descendantPalette ? () => descendantPalette : undefined,
        trigger: accept.includes('page')
            ? () => {
                  pick(createPageRef(page.id));
              }
            : undefined,
    });

    if (!attrs) {
        return null;
    }

    return {
        key: page.id,
        title:
            page.linkTitle && page.linkTitle !== page.title
                ? `${page.linkTitle} (${page.title})`
                : page.title,
        subtitle: () => pagePaths.get(page.id) ?? '',
        icon: page.type === hiveCore.RevisionPageType.Group ? Icon.AddPageGroup : Icon.Page,
        ...attrs,
    } satisfies PaletteCommandDef;
}

/**
 * Get palette attributes with correct typing.
 */
function getPaletteAttributes(args: {
    trigger: PaletteCommandTrigger | undefined;
    descendant: (() => PaletteDef) | undefined;
}) {
    const { trigger, descendant } = args;
    if (trigger) {
        if (descendant) {
            return { trigger, descendant };
        }
        return { trigger };
    }
    if (descendant) {
        return { descendant };
    }
    return null;
}

/** Generate commands to select an anchor in a page */
function getAnchorCommands(args: {
    page: hiveCore.RevisionPageDocument;
    context: ContentRefPaletteContext;
    spaceContext: SpacePaletteContext;
}): PaletteCommandDef[] {
    const { page, context, spaceContext } = args;
    const { hiveClient, pick } = context;
    const { spaceId, changeRequestId, createPageRef } = spaceContext;
    const document = getSuspense(
        hiveClient.store.getBranchPageDocument(
            spaceId,
            changeRequestId
                ? getChangeRequestBranchId(changeRequestId)
                : hiveCore.CONTENT_DEFAULT_BRANCH,
            page,
            { checkBranchExists: false }
        )
    );
    if (!document) {
        return [];
    }

    const pageRef = createPageRef(page.id);

    return getAnchorCommandsForDoc(document, { ...pageRef, kind: 'anchor' }, pick);
}

/** Generate commands to select an anchor in a document */
function getAnchorCommandsForDoc(
    document: Document,
    anchorContext: Partial<api.ContentRefAnchor>,
    pick: (link: api.ContentRefAnchor) => any
): PaletteCommandDef[] {
    const sections = getSectionsInDoc(document);

    return sections
        .filter((section) => Boolean(section.anchor))
        .map((section) => {
            const icon = (() => {
                switch (section.type) {
                    case BLOCK_HEADING_1:
                        return Icon.Heading1;
                    case BLOCK_HEADING_2:
                        return Icon.Heading2;
                    case BLOCK_HEADING_3:
                        return Icon.Heading3;
                    case BLOCK_OPENAPI:
                    case BLOCK_OPENAPI_OPERATION:
                    case BLOCK_OPENAPI_SCHEMAS:
                    case BLOCK_OPENAPI_WEBHOOK:
                        return Icon.OpenAPI;
                    case BLOCK_EXPANDABLE:
                        return Icon.BlockExpandable;
                    case BLOCK_TABS_ITEM:
                    case BLOCK_TABS:
                        return Icon.Tabs;
                    case BLOCK_UPDATE:
                        return Icon.Calendar;
                    case null:
                        return Icon.Hash;
                    default:
                        assertNever(section.type);
                }
            })();

            return {
                key: section.key,
                icon,
                title: section.title,
                trigger: () => {
                    pick({ kind: 'anchor', anchor: section.anchor, ...anchorContext });
                },
            };
        });
}

/**
 * Get the palette group from a site.
 */
function getSitePaletteGroup(
    context: ContentRefPaletteContext,
    commandsCache: SiteStructureCommandsCache
): PaletteGroupDef | null {
    const { site: siteId, accept } = context;
    if (
        !siteId ||
        !accept.some((kind) => kind === 'space' || kind === 'page' || kind === 'anchor')
    ) {
        return null;
    }

    return {
        title: 'Content',
        key: 'site-structure',
        commands: (query) => {
            const siteStructure = readSiteStructure(context);

            if (!siteStructure) {
                return [];
            }

            if (!query) {
                return [];
            }

            const drilldown = resolveSiteStructureDrilldownQuery({
                rootCommands: commandsCache.getRootCommands(siteStructure),
                query,
            });
            if (drilldown) {
                return getPaletteCommandsForPathQuery({
                    palette: drilldown.palette,
                    parentTitles: drilldown.parentTitles,
                    query: drilldown.query,
                    mode: 'content',
                });
            }

            return commandsCache.getFlatCommands(siteStructure, query);
        },
    };
}

function getSiteHeadingsPaletteGroup(
    context: ContentRefPaletteContext,
    commandsCache: SiteStructureCommandsCache
): PaletteGroupDef | null {
    if (!context.accept.includes('anchor')) {
        return null;
    }

    return {
        title: 'Headings & tabs',
        key: 'site-structure-headings',
        commands: (query) => {
            const siteStructure = readSiteStructure(context);

            if (!siteStructure) {
                return [];
            }

            const drilldown = resolveSiteStructureDrilldownQuery({
                rootCommands: commandsCache.getRootCommands(siteStructure),
                query,
            });
            if (!drilldown) {
                return [];
            }

            return getPaletteCommandsForPathQuery({
                palette: drilldown.palette,
                parentTitles: drilldown.parentTitles,
                query: drilldown.query,
                mode: 'headings',
            });
        },
    };
}

function readSiteStructure(context: ContentRefPaletteContext): api.SiteStructure | null {
    const { site: siteId, organization: organizationId } = context;
    if (!siteId) {
        return null;
    }

    const allContent = readAPICache(apiResource('getOrganizationAllContent', { organizationId }), {
        staleWhileRevalidate: true,
    });

    // First try to extract it from the org all content in cache
    const siteData = allContent.sites.find((data) => data.site.id === siteId);

    if (siteData) {
        return siteData.structure;
    }

    // If it does not exist, fetch it
    return readAPICache(
        apiResource('getSiteStructure', { organizationId, siteId }, { nullOnError: true })
    );
}

/**
 * Get the commands from a site structure.
 */
function getSiteStructureCommands(args: {
    siteStructure: api.SiteStructure;
    context: ContentRefPaletteContext;
}): PaletteCommandDef[] {
    const { siteStructure, context } = args;
    switch (siteStructure.type) {
        case 'sections':
            return excludeSiteExternalLinks(siteStructure.structure)
                .map((item) =>
                    item.object === 'site-section-group'
                        ? createSiteSectionGroupCommand({ siteSectionGroup: item, context })
                        : createSiteSectionCommand({ siteSection: item, context })
                )
                .filter(filterOutNullable);
        case 'siteSpaces':
            return siteStructure.structure
                .map((siteSpace) => createSiteSpaceCommand({ siteSpace, context }))
                .filter(filterOutNullable);
        default:
            assertNever(siteStructure);
    }
}

function getFlatSiteStructureCommands(args: {
    siteStructure: api.SiteStructure;
    context: ContentRefPaletteContext;
}): PaletteCommandDef[] {
    const { siteStructure, context } = args;
    // A space published in several places of a site resolves to the same page refs each time,
    // so its pages are flattened from its first appearance only.
    const flattenedSpaces = new Set<string>();

    switch (siteStructure.type) {
        case 'sections':
            // `mapSuspenseArray` (not `flatMap`) so that every section's page fetch is
            // gathered into a single `Promise.all` and awaited in one suspense round. A
            // plain `flatMap` throws on the first unresolved section, turning a large site
            // into an N-deep serial fetch waterfall.
            return mapSuspenseArray(excludeSiteExternalLinks(siteStructure.structure), (item) => {
                switch (item.object) {
                    case 'site-section-group':
                        return getFlatSiteSectionGroupCommands({
                            siteSectionGroup: item,
                            context,
                            flattenedSpaces,
                        });
                    case 'site-section':
                        return getFlatSiteSectionCommands({
                            siteSection: item,
                            context,
                            flattenedSpaces,
                        });
                    default:
                        return assertNever(item);
                }
            }).flat();
        case 'siteSpaces':
            // See the comment above: batch every space's page fetch into one suspense round.
            return mapSuspenseArray(siteStructure.structure, (siteSpace) =>
                getFlatSiteSpaceCommands({
                    siteSpace,
                    context,
                    flattenedSpaces,
                })
            ).flat();
        default:
            assertNever(siteStructure);
    }
}

type ResolvedPathQuery = {
    palette: PaletteDef;
    parentTitles: string[];
    query: string;
};

/**
 * Cache for the two derived site-structure command trees, scoped to a single palette
 * instance.
 *
 * A palette re-reads the structure and re-derives its commands on every keystroke, and
 * both the content and headings groups need the drill-down tree — so without a cache a
 * large site's tree would be rebuilt several times per keystroke. We build each tree once
 * and reuse it for the palette's lifetime, rebuilding only when the structure's content
 * actually changes.
 */
type SiteStructureCommandsCache = {
    /** Root commands used for `parent > child` drill-down resolution. */
    getRootCommands: (siteStructure: api.SiteStructure) => PaletteCommandDef[];
    /**
     * Flattened commands for the default (non-drill-down) search. The expensive flat list
     * is cached per structure, then filtered with deterministic content-reference matching.
     */
    getFlatCommands: (siteStructure: api.SiteStructure, query: string) => PaletteCommandDef[];
};

type SiteStructureCommandsCacheEntry = {
    siteStructure: api.SiteStructure;
    commands: PaletteCommandDef[];
};

/**
 * Build the per-palette cache that memoizes the derived site-structure command trees.
 *
 * Kept as a standalone factory so the memoization contract (build once per structure
 * content, rebuild when the content changes) can be exercised in isolation.
 */
export function createSiteStructureCommandsCache(
    context: ContentRefPaletteContext
): SiteStructureCommandsCache {
    let rootCache: SiteStructureCommandsCacheEntry | null = null;
    let flatCache: SiteStructureCommandsCacheEntry | null = null;

    // Resolve a cache entry for `siteStructure`, building the command tree only when the
    // structure's content changed. The object-identity check is the fast path hit on every
    // keystroke; the deep-equality check absorbs stale-while-revalidate refreshes that return
    // an equivalent structure under a new identity, so the whole tree is not rebuilt then.
    const resolve = (
        cache: SiteStructureCommandsCacheEntry | null,
        siteStructure: api.SiteStructure,
        build: () => PaletteCommandDef[]
    ): SiteStructureCommandsCacheEntry => {
        if (cache?.siteStructure === siteStructure) {
            return cache;
        }
        if (cache && equal(cache.siteStructure, siteStructure)) {
            return { siteStructure, commands: cache.commands };
        }
        return { siteStructure, commands: build() };
    };

    return {
        getRootCommands(siteStructure) {
            rootCache = resolve(rootCache, siteStructure, () =>
                getSiteStructureCommands({ siteStructure, context })
            );
            return rootCache.commands;
        },
        getFlatCommands(siteStructure, query) {
            // Build the full flattened list once, independent of the query, so it can be
            // reused across queries. Filtering happens per query below, instead of freezing
            // the cache to the first query's matches.
            flatCache = resolve(flatCache, siteStructure, () =>
                getFlatSiteStructureCommands({ siteStructure, context })
            );
            return filterContentReferenceCommands(query, flatCache.commands);
        },
    };
}

/**
 * Filter content-reference commands with deterministic substring matching.
 * Content pickers search page titles, visible paths, and parent titles; fuzzy edit-distance
 * matching makes large flattened sites return barely related pages.
 */
export function filterContentReferenceCommands(
    query: string,
    commands: PaletteCommandDef[]
): PaletteCommandDef[] {
    const tokens = tokenizeContentReferenceSearch(query);
    if (tokens.length === 0) {
        return commands;
    }

    return commands.filter((command) => {
        const searchableValues = getContentReferenceCommandSearchableValues(command);
        return tokens.every((token) => searchableValues.some((value) => value.includes(token)));
    });
}

/**
 * Cache of normalized searchable strings per command. The flat command list is cached and
 * reused across keystrokes, so the NFD/regex normalization of each command's title,
 * subtitle, parent titles, and keywords runs once per command rather than on every keypress.
 */
const commandSearchableValuesCache = new WeakMap<PaletteCommandDef, string[]>();

function getContentReferenceCommandSearchableValues(command: PaletteCommandDef): string[] {
    const cached = commandSearchableValuesCache.get(command);
    if (cached) {
        return cached;
    }

    const subtitle = typeof command.subtitle === 'function' ? command.subtitle() : command.subtitle;
    const searchableValues = [
        command.title,
        subtitle,
        ...(command.parentTitles ?? []),
        ...(command.keywords ?? []),
    ]
        .filter((value): value is string => Boolean(value))
        .map(normalizeContentReferenceSearchValue);

    commandSearchableValuesCache.set(command, searchableValues);
    return searchableValues;
}

function tokenizeContentReferenceSearch(query: string): string[] {
    const normalizedQuery = normalizeContentReferenceSearchValue(query);
    return normalizedQuery ? normalizedQuery.split(' ') : [];
}

function normalizeContentReferenceSearchValue(value: string): string {
    return value
        .trim()
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}

/**
 * Resolve a `parent > child > query` drill-down against the site structure.
 * Delegates path parsing and segment matching to the shared uikit resolver, then
 * flattens its drill-down stack into the parent titles the site groups use for
 * breadcrumbs and keyword matching.
 */
function resolveSiteStructureDrilldownQuery(args: {
    rootCommands: PaletteCommandDef[];
    query: string;
}): ResolvedPathQuery | null {
    const { rootCommands, query } = args;
    const pathQuery = parsePathQuery(query);
    if (!pathQuery) {
        return null;
    }

    let palette = createPalette({
        groups: [
            {
                key: 'site-structure-root',
                commands: rootCommands,
            },
        ],
    });
    const parentTitles: string[] = [];

    for (const segment of pathQuery.segments) {
        const commands = getPaletteGroupCommandsForQuery({
            groups: palette.groups,
            query: segment,
        });
        if (!commands) {
            return null;
        }

        const command = resolvePathSegmentCommand(commands, segment);
        if (!command?.descendant) {
            return null;
        }

        parentTitles.push(command.title);
        palette = command.descendant();
    }

    return {
        palette,
        parentTitles,
        query: pathQuery.query,
    };
}

function getPaletteCommandsForPathQuery(args: {
    palette: PaletteDef;
    parentTitles: string[];
    query: string;
    mode: 'content' | 'headings';
}): PaletteCommandDef[] {
    const { palette, parentTitles, query, mode } = args;

    return palette.groups.flatMap((group) => {
        const isHeadingsGroup = group.key === HEADINGS_GROUP_KEY;
        if (mode === 'headings' ? !isHeadingsGroup : isHeadingsGroup) {
            return [];
        }

        const commands =
            getPaletteGroupCommandsForQuery({
                groups: [group],
                query,
            }) ?? [];
        const filteredCommands = query ? filterContentReferenceCommands(query, commands) : commands;

        return filteredCommands.map((command) =>
            withPathQueryMetadata({
                command,
                parentTitles,
                keyPrefix: `path-${mode}-${group.key}`,
            })
        );
    });
}

function withPathQueryMetadata(args: {
    command: PaletteCommandDef;
    parentTitles: string[];
    keyPrefix: string;
}): PaletteCommandDef {
    const { command, parentTitles, keyPrefix } = args;
    const allParentTitles = [...parentTitles, ...(command.parentTitles ?? [])];
    const fullPath = [...allParentTitles, command.title].join(' > ');
    const searchablePath = [...allParentTitles, command.title].join(' ');

    return {
        ...command,
        key: `${keyPrefix}-${command.key}`,
        parentTitles: allParentTitles,
        keywords: [...(command.keywords ?? []), ...allParentTitles, fullPath, searchablePath],
    };
}

function parsePathQuery(query: string): { segments: string[]; query: string } | null {
    if (!query.includes('>')) {
        return null;
    }

    const parts = query.split('>');
    const segments = parts.slice(0, -1).map((part) => part.trim());

    if (segments.some((segment) => segment.length === 0)) {
        return null;
    }

    return {
        segments,
        query: parts.at(-1)?.trimStart() ?? '',
    };
}

function getPaletteGroupCommandsForQuery(args: {
    groups: PaletteGroupDef[];
    query: string;
}): PaletteCommandDef[] | null {
    const { groups, query } = args;
    try {
        return groups.flatMap((group) =>
            typeof group.commands === 'function' ? group.commands(query) : group.commands
        );
    } catch (error) {
        if (error instanceof Promise) {
            return null;
        }
        throw error;
    }
}

function resolvePathSegmentCommand(
    commands: PaletteCommandDef[],
    segment: string
): PaletteCommandDef | null {
    const normalizedSegment = normalizePathSegment(segment);
    if (!normalizedSegment) {
        return null;
    }

    const navigableCommands = commands.filter((command) => Boolean(command.descendant));
    const exactMatch = getUniqueShallowestPathSegmentMatch(
        navigableCommands,
        (command) => normalizePathSegment(command.title) === normalizedSegment
    );
    if (exactMatch) {
        return exactMatch;
    }

    const partialMatch = getUniqueShallowestPathSegmentMatch(
        navigableCommands,
        (command) =>
            normalizePathSegment(command.title).includes(normalizedSegment) &&
            !normalizePathSegment(command.title).startsWith(`${normalizedSegment} >`)
    );
    if (partialMatch) {
        return partialMatch;
    }

    return null;
}

function getUniqueShallowestPathSegmentMatch(
    commands: PaletteCommandDef[],
    predicate: (command: PaletteCommandDef) => boolean
): PaletteCommandDef | null {
    const matches = commands.filter(predicate);
    if (matches.length === 0) {
        return null;
    }

    const shallowestDepth = Math.min(
        ...matches.map((command) => command.parentTitles?.length ?? 0)
    );
    const shallowestMatches = matches.filter(
        (command) => (command.parentTitles?.length ?? 0) === shallowestDepth
    );

    return shallowestMatches.length === 1 ? shallowestMatches[0] : null;
}

function normalizePathSegment(value: string) {
    return value.trim().toLocaleLowerCase();
}

/**
 * Sort organization all items.
 * Spaces first, then collections.
 */
function sortOrgAllItems(value: Array<api.OrganizationAllCollection | api.OrganizationAllSpace>) {
    return Array.from(value).sort((a, b) => {
        const aIsSpace = 'space' in a ? 1 : 0;
        const bIsSpace = 'space' in b ? 1 : 0;
        return bIsSpace - aIsSpace;
    });
}

type CreateOrganizationContentCommandsArgs = {
    items: CreateOrganizationContentCommandArgs['item'][];
    context: ContentRefPaletteContext;
    query?: string;
};

function createOrganizationContentCommands(
    args: CreateOrganizationContentCommandsArgs
): PaletteCommandDef[] {
    const { items, query, context } = args;

    // If we are in search mode, we return all items flat,
    // menu will take care of search.
    if (query) {
        return getFlatOrgItemCommands({ items, context });
    }

    return sortOrgAllItems(items).reduce<PaletteCommandDef[]>((all, item) => {
        const command = createOrganizationContentCommand({ item, context });
        if (command) {
            all.push(command);
        }
        return all;
    }, []);
}

type CreateOrganizationContentCommandArgs = {
    item: api.OrganizationAllCollection | api.OrganizationAllSpace;
    context: ContentRefPaletteContext;
};

function createOrganizationContentCommand(
    input: CreateOrganizationContentCommandArgs
): PaletteCommandDef | null {
    const { item, context } = input;

    if ('collection' in item) {
        const subCommands = createOrganizationContentCommands({
            items: item.content,
            context,
        });

        if (subCommands.length === 0) {
            return null;
        }

        return createCollectionCommand({
            collection: item.collection,
            descendant: () =>
                createPalette({
                    groups: [
                        {
                            key: `collection-${item.collection.id}`,
                            commands: subCommands,
                        },
                    ],
                }),
        });
    }

    // Do not list the current space
    if (item.space.id === context.space) {
        return null;
    }

    return createSpaceCommand({
        space: item.space,
        context,
    });
}

/**
 * Flatten all org items convert them to commands.
 */
function getFlatOrgItemCommands(args: {
    items: (api.OrganizationAllCollection | api.OrganizationAllSpace)[];
    context: ContentRefPaletteContext;
    ancestors?: api.OrganizationAllCollection[];
}) {
    const { items, context, ancestors = [] } = args;
    return items.reduce<PaletteCommandDef[]>((commands, item) => {
        const command = createOrganizationContentCommand({
            item,
            context,
        });

        if (command) {
            commands.push({
                ...command,
                parentTitles: ancestors.map((ancestor) => ancestor.collection.title),
            });

            if ('collection' in item) {
                commands.push(
                    ...getFlatOrgItemCommands({
                        items: item.content,
                        context,
                        ancestors: [...ancestors, item],
                    })
                );
            }
        }

        return commands;
    }, []);
}

/**
 * Create a command for a collection.
 */
function createCollectionCommand(args: {
    collection: api.Collection;
    descendant: () => PaletteDef;
}) {
    const { descendant, collection } = args;

    return {
        key: collection.id,
        title: collection.title,
        icon: <CollectionIcon />,
        descendant,
    } satisfies PaletteCommandDef;
}

/**
 * Create a command for a space.
 */
function createSpaceCommand(args: {
    space: api.Space;
    context: ContentRefPaletteContext;
}): PaletteCommandDef | null {
    const { space, context } = args;
    const { accept, pick } = context;

    const descendant =
        accept.includes('page') || accept.includes('anchor')
            ? () =>
                  createSpaceDescendantPalette({
                      space,
                      context,
                      changeRequestId: undefined,
                  })
            : undefined;

    return {
        key: space.id,
        icon: <SpaceIcon space={space} />,
        title: space.title,
        trigger: () => pick({ kind: 'space', space: space.id }),
        descendant,
    } satisfies PaletteCommandDef;
}

function createSpaceDescendantPalette(args: {
    space: api.Space;
    context: ContentRefPaletteContext;
    changeRequestId: string | undefined;
}): PaletteDef {
    const { space, context, changeRequestId } = args;
    const groups: PaletteGroupDef[] = [
        context.accept.includes('space')
            ? {
                  key: `space-${space.id}-pick`,
                  commands: [
                      {
                          key: 'pick',
                          title: `Link to "${space.title}"`,
                          icon: <SpaceIcon space={space} />,
                          trigger: () => context.pick({ kind: 'space', space: space.id }),
                      },
                  ],
              }
            : null,
        createSpacePagesGroup({
            content: {
                spaceId: space.id,
                changeRequestId,
            },
            context,
        }),
    ].filter(filterOutNullable);

    return createPalette({
        search: { placeholder: 'Search page…' },
        groups,
    });
}

function orderPagesWithFirst(pages: hiveCore.RevisionPage[], firstPageId: string) {
    const index = pages.findIndex((page) => page.id === firstPageId);
    if (index <= 0) {
        return pages;
    }

    const next = pages.slice();
    const [page] = next.splice(index, 1);
    return [page, ...next];
}

function createSiteSectionGroupCommand(args: {
    siteSectionGroup: api.SiteSectionGroup;
    context: ContentRefPaletteContext;
}): PaletteCommandDef | null {
    const { siteSectionGroup, context } = args;
    const commands = siteSectionGroup.children.reduce<PaletteCommandDef[]>((all, child) => {
        if (child.object === 'site-external-link') {
            return all;
        }
        const command =
            child.object === 'site-section-group'
                ? createSiteSectionGroupCommand({ siteSectionGroup: child, context })
                : createSiteSectionCommand({ siteSection: child, context });
        if (command) {
            all.push(command);
        }
        return all;
    }, []);

    if (commands.length === 0) {
        return null;
    }

    return {
        key: siteSectionGroup.id,
        title: siteSectionGroup.title,
        icon: <SiteSectionGroupIcon siteSectionGroup={siteSectionGroup} />,
        descendant: () =>
            createPalette({
                groups: [
                    {
                        key: `site-section-group-${siteSectionGroup.id}`,
                        commands,
                    },
                ],
            }),
    };
}

function getFlatSiteSectionGroupCommands(args: {
    siteSectionGroup: api.SiteSectionGroup;
    context: ContentRefPaletteContext;
    flattenedSpaces: Set<string>;
    ancestors?: string[];
}): PaletteCommandDef[] {
    const { siteSectionGroup, context, flattenedSpaces, ancestors = [] } = args;
    const command = createSiteSectionGroupCommand({ siteSectionGroup, context });
    const parentTitles = ancestors;
    // `mapSuspenseArray` so nested section fetches batch into one suspense round instead
    // of suspending serially child-by-child.
    const children = mapSuspenseArray(
        excludeSiteExternalLinks(siteSectionGroup.children),
        (child) =>
            child.object === 'site-section-group'
                ? getFlatSiteSectionGroupCommands({
                      siteSectionGroup: child,
                      context,
                      flattenedSpaces,
                      ancestors: [...ancestors, siteSectionGroup.title],
                  })
                : getFlatSiteSectionCommands({
                      siteSection: child,
                      context,
                      flattenedSpaces,
                      ancestors: [...ancestors, siteSectionGroup.title],
                  })
    ).flat();

    return [
        command
            ? withParentTitles(command, parentTitles, `site-section-group-${command.key}`)
            : null,
        ...children,
    ].filter(filterOutNullable);
}

function createSiteSectionCommand(args: {
    siteSection: api.SiteSection;
    context: ContentRefPaletteContext;
}): PaletteCommandDef | null {
    const { siteSection, context } = args;
    const { accept, pick } = context;
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;

    // If section has only one space, flatten it like in the navbar
    if (singleSiteSpace) {
        const attrs = getPaletteAttributes({
            trigger: accept.includes('space')
                ? () => {
                      pick({ kind: 'space', space: singleSiteSpace.space.id });
                  }
                : undefined,
            descendant:
                accept.includes('page') || accept.includes('anchor')
                    ? () => ({
                          ...createSpaceDescendantPalette({
                              space: singleSiteSpace.space,
                              context,
                              changeRequestId:
                                  'changeRequest' in context &&
                                  context.space === singleSiteSpace.space.id
                                      ? context.changeRequest
                                      : undefined,
                          }),
                          position: {
                              preferredPlacement: 'right',
                              preferredAlignment: 'start',
                          },
                      })
                    : undefined,
        });

        if (attrs === null) {
            return null;
        }

        return {
            key: siteSection.id,
            title: siteSection.title,
            icon: <SiteSectionIcon siteSection={siteSection} />,
            ...attrs,
        } satisfies PaletteCommandDef;
    }

    // Multiple spaces - show variants
    const commands = siteSection.siteSpaces.reduce<PaletteCommandDef[]>((all, siteSpace) => {
        const command = createSiteSpaceCommand({ siteSpace, context });
        if (command) {
            all.push(command);
        }
        return all;
    }, []);

    if (commands.length === 0) {
        return null;
    }

    return {
        key: siteSection.id,
        title: siteSection.title,
        icon: <SiteSectionIcon siteSection={siteSection} />,
        descendant: () =>
            createPalette({
                groups: [
                    {
                        key: `site-section-${siteSection.id}`,
                        commands,
                    },
                ],
            }),
    };
}

function getFlatSiteSectionCommands(args: {
    siteSection: api.SiteSection;
    context: ContentRefPaletteContext;
    flattenedSpaces: Set<string>;
    ancestors?: string[];
}): PaletteCommandDef[] {
    const { siteSection, context, flattenedSpaces, ancestors = [] } = args;
    const command = createSiteSectionCommand({ siteSection, context });
    const parentTitles = ancestors;
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;
    // `mapSuspenseArray` so every space in the section fetches its pages concurrently in
    // one suspense round rather than one serial round-trip per space.
    const siteSpaceCommands = mapSuspenseArray(siteSection.siteSpaces, (siteSpace) =>
        getFlatSiteSpaceCommands({
            siteSpace,
            context,
            flattenedSpaces,
            ancestors: [...ancestors, siteSection.title],
            includeSiteSpaceCommand: !singleSiteSpace,
        })
    ).flat();

    return [
        command ? withParentTitles(command, parentTitles, `site-section-${command.key}`) : null,
        ...siteSpaceCommands,
    ].filter(filterOutNullable);
}

function createSiteSpaceCommand(args: {
    siteSpace: api.SiteSpace;
    context: ContentRefPaletteContext;
}): PaletteCommandDef | null {
    const { siteSpace, context } = args;
    const { accept, pick } = context;

    const attrs = getPaletteAttributes({
        descendant:
            accept.includes('page') || accept.includes('anchor')
                ? () => ({
                      ...createSpaceDescendantPalette({
                          space: siteSpace.space,
                          context,
                          changeRequestId:
                              'changeRequest' in context && context.space === siteSpace.space.id
                                  ? context.changeRequest
                                  : undefined,
                      }),
                      position: {
                          preferredPlacement: 'right',
                          preferredAlignment: 'start',
                      },
                  })
                : undefined,
        trigger: accept.includes('space')
            ? () => {
                  pick({ kind: 'space', space: siteSpace.space.id });
              }
            : undefined,
    });

    if (!attrs) {
        return null;
    }

    return {
        key: siteSpace.id,
        title: siteSpace.title,
        icon: <SpaceIcon space={siteSpace.space} />,
        ...attrs,
    } satisfies PaletteCommandDef;
}

function getFlatSiteSpaceCommands(args: {
    siteSpace: api.SiteSpace;
    context: ContentRefPaletteContext;
    flattenedSpaces: Set<string>;
    ancestors?: string[];
    includeSiteSpaceCommand?: boolean;
}): PaletteCommandDef[] {
    const {
        siteSpace,
        context,
        flattenedSpaces,
        ancestors = [],
        includeSiteSpaceCommand = true,
    } = args;
    const spaceId = siteSpace.space.id;
    const isRepeatedSpace = flattenedSpaces.has(spaceId);
    flattenedSpaces.add(spaceId);
    const command = includeSiteSpaceCommand
        ? createSiteSpaceCommand({
              siteSpace,
              context,
          })
        : null;

    // The current space's pages are already listed, and ranked higher, by the current-space group.
    const hasCurrentSpaceGroup = context.space === spaceId && context.accept.includes('page');

    if (
        isRepeatedSpace ||
        hasCurrentSpaceGroup ||
        (!context.accept.includes('page') && !context.accept.includes('anchor'))
    ) {
        return command ? [withParentTitles(command, ancestors, `site-space-${command.key}`)] : [];
    }

    const pagesGroup = createSpacePagesGroup({
        content: {
            spaceId,
            changeRequestId: getSiteSpaceChangeRequestId({ siteSpace, context }),
        },
        context,
        // Build the full flattened page list; the cached flat command list is filtered
        // per query by the caller (getFlatCommands), not here.
        flatten: true,
    });
    const pageCommands =
        typeof pagesGroup.commands === 'function' ? pagesGroup.commands('') : pagesGroup.commands;
    const pageParentTitles = [...ancestors, siteSpace.title];

    return [
        command ? withParentTitles(command, ancestors, `site-space-${command.key}`) : null,
        ...pageCommands.map((pageCommand) =>
            withParentTitles(pageCommand, pageParentTitles, `site-space-${siteSpace.id}`)
        ),
    ].filter(filterOutNullable);
}

function getSiteSpaceChangeRequestId(args: {
    siteSpace: api.SiteSpace;
    context: ContentRefPaletteContext;
}): string | undefined {
    const { siteSpace, context } = args;
    return 'changeRequest' in context && context.space === siteSpace.space.id
        ? context.changeRequest
        : undefined;
}

/**
 * Rank offset added to a matched site-structure command's `filterPriority`.
 *
 * In search mode the palette menu flattens every group into one list, pins the commands that
 * carry a `filterPriority`, and sorts those ascending (lower sorts first). Current-page and
 * current-space matches keep their small natural priority (see {@link getDirectTitleMatchPriority},
 * bands 0–2xx), so bumping every site-wide match into a much higher band guarantees local matches
 * always rank above site-wide ones — regardless of relative fuzzy-match quality.
 */
const SITE_CONTENT_RANK = 10_000;

function withParentTitles(
    command: PaletteCommandDef,
    parentTitles: string[],
    keyPrefix: string
): PaletteCommandDef {
    return {
        ...command,
        key: `${keyPrefix}-${command.key}`,
        parentTitles: [...parentTitles, ...(command.parentTitles ?? [])],
        filterPriority:
            command.filterPriority !== undefined
                ? command.filterPriority + parentTitles.length + SITE_CONTENT_RANK
                : undefined,
    };
}

function getDirectTitleMatchPriority(args: {
    title: string;
    query: string;
    depth: number;
}): number | undefined {
    const { title, query, depth } = args;
    const normalizedTitle = normalizeSearchValue(title);
    const normalizedQuery = normalizeSearchValue(query);

    if (!normalizedQuery || !normalizedTitle.includes(normalizedQuery)) {
        return undefined;
    }

    // Keep direct title matches ahead of path-only matches, then prefer shallower paths.
    if (normalizedTitle === normalizedQuery) {
        return depth;
    }

    if (normalizedTitle.startsWith(normalizedQuery)) {
        return 100 + depth;
    }

    return 200 + depth;
}

function normalizeSearchValue(value: string) {
    return value.trim().toLocaleLowerCase();
}

/**
 * Cache for page paths map computation, keyed by revision id
 */
const pagePathsMapCache = createCache({
    getValue: async (revision: hiveCore.Revision) => {
        return hiveCore.getAbsolutePagePathsMap(revision);
    },
    getKey: (revision: hiveCore.Revision) => revision.id,
});

/**
 * Create a cache of computed revisions, keyed by content and source-revision id.
 *
 * `getContentComputer` hands out ContentComputer instances from a small shared LRU, and the
 * resolved computation lives on that instance. The palette flattens one computer per space,
 * so a site with more spaces than the cache holds evicts instances mid-flatten — a Suspense
 * retry then recreates a cold computer that suspends again, looping forever (only computed/
 * translated spaces trigger it, since plain spaces resolve synchronously). Caching the
 * resolved revision here, independent of the evictable instance, lets a retry resolve
 * synchronously so the flatten converges regardless of the shared computer cache size.
 *
 * Scoped to a single palette instance (created in `createInputContentRefPalette`), not
 * module-level: it only needs to survive the Suspense retries of one flatten and is released
 * when the palette closes. Computed content is otherwise kept live on the ContentComputer via
 * subscriptions, so a session-long cache here would both grow unbounded and risk serving stale
 * computed content — the per-palette scope keeps memory bounded and each open fresh.
 *
 * `getValue` uses `getCompleteRevision` (a `MaybePromise`), not `getCompleteRevisionAsync`, so
 * a plain (non-computed) space still resolves synchronously — `createCache` writes it straight
 * to the cache without suspending — rather than paying an extra Suspense round per space.
 */
export function createCompleteRevisionCache() {
    return createCache({
        getValue: ([hiveClient, sourceRevision]: readonly [HiveClient, hiveCore.Revision]) =>
            hiveClient
                .getContentComputer(sourceRevision.contentId)
                .getCompleteRevision(sourceRevision),
        getKey: ([, sourceRevision]: readonly [HiveClient, hiveCore.Revision]) =>
            `${sourceRevision.contentId}:${sourceRevision.id}`,
    });
}

type KindToContent = {
    [C in api.ContentRef as C['kind']]: C;
};

const defaultAccept: ContentRefKind[] = ['anchor', 'page', 'space', 'file', 'user', 'url'];

type CreateInputContentRefPaletteExtraGroup<T extends ContentRefKind[] = ContentRefKind[]> =
    | PaletteGroupDef
    | ((pick: (link: KindToContent[T[number]]) => any) => PaletteGroupDef);
